import removeAccents from 'remove-accents';
import { colors } from '@karnott/colors';
import { BatteryIcon, BeaconIcon, KarnottIcon } from '@karnott/icons';
import { DEVICE_CONSTANTS, DeviceChargingState, DeviceStatus, DeviceType } from '../constants/deviceConstants';
import { NormalizedBeacon } from '../slices/beacons';
import { DeviceModelId, NormalizedDevice } from '../slices/devices';

type DeviceWithHealth = Pick<NormalizedDevice, 'health'> & { deviceType?: DeviceType };

export const deviceContains = (device: NormalizedDevice, filter: string) =>
  removeAccents(device.name || '')
    .toLowerCase()
    .includes(removeAccents(filter).toLowerCase()) || device.serialnumber.includes(filter);

export const beaconsContains = (beacon: NormalizedBeacon, filter: string) =>
  removeAccents(beacon.uuid || '')
    .toLowerCase()
    .includes(removeAccents(filter).toLowerCase());

export const getBatteryLevel = (device?: NormalizedDevice | NormalizedBeacon) => {
  if (!device) return undefined;
  if ('deviceModel' in device) {
    return Math.max(0, Math.min(device.deviceModelId === 0 ? device.batteryLevel : device.externalBatteryLevel, 100));
  }
  return device.soc;
};

export const getBatteryLevelIcon = (
  level = 0,
  isCharging?: boolean,
  outlineColor = colors('black'),
  chargingColor = outlineColor,
) => {
  if (level > 100) {
    level = 100;
  }
  return (
    <BatteryIcon
      batteryLevel={level}
      batteryColor={getBatteryLevelColor(level)}
      charging={isCharging}
      chargingColor={chargingColor}
      color={level === 0 ? colors('red') : outlineColor}
      size={20}
    />
  );
};

const getBatteryLevelColor = (level: number) => {
  let color = DEVICE_CONSTANTS.BATTERY.COLOR.HIGH_LEVEL_COLOR;
  if (level <= DEVICE_CONSTANTS.BATTERY.LEVEL.LOW_STEP && level > DEVICE_CONSTANTS.BATTERY.LEVEL.CRITICAL_STEP) {
    color = DEVICE_CONSTANTS.BATTERY.COLOR.LOW_LEVEL_COLOR;
  } else if (level <= DEVICE_CONSTANTS.BATTERY.LEVEL.CRITICAL_STEP) {
    color = DEVICE_CONSTANTS.BATTERY.COLOR.CRITICAL_LEVEL_COLOR;
  }
  return color;
};

export const getDeviceStatusIcon = (
  type: DeviceType = DEVICE_CONSTANTS.TYPE.KARNOTT,
  status: DeviceStatus,
  size = 20,
  hasHealthIssue?: boolean,
) => {
  if (type === DEVICE_CONSTANTS.TYPE.BEACON) {
    return (
      <BeaconIcon
        circled
        backgroundColor={status === DEVICE_CONSTANTS.STATUS.IN_USE ? colors('green') : colors('black')}
        color={colors('white')}
        size={size}
      />
    );
  } else {
    switch (status) {
      case DEVICE_CONSTANTS.STATUS.IN_USE:
        return (
          <KarnottIcon
            circled
            backgroundColor={colors('green')}
            color={colors('white')}
            healthIssue={hasHealthIssue}
            size={size}
          />
        );
      default:
        return (
          <KarnottIcon
            circled
            backgroundColor={colors('black', 'dark')}
            color={colors('white')}
            healthIssue={hasHealthIssue}
            size={size}
          />
        );
    }
  }
};

export const getBatteryHealth = (device?: DeviceWithHealth) => device?.health?.battery?.status;
export const hasBadBatteryHealth = (device?: DeviceWithHealth) =>
  getBatteryHealth(device) !== DEVICE_CONSTANTS.BATTERY.HEALTH_STATUS.OK;

export const getDeviceHealth = (device?: DeviceWithHealth) => device?.health?.status;
export const hasCriticalHealth = (device?: DeviceWithHealth) => {
  return (
    !!device &&
    device.deviceType !== DEVICE_CONSTANTS.TYPE.BEACON &&
    getDeviceHealth(device) !== DEVICE_CONSTANTS.HEALTH_STATUS.UP
  );
};

export function isCharging(chargingState?: DeviceChargingState) {
  return (
    !!chargingState &&
    (chargingState === DEVICE_CONSTANTS.CHARGING_STATE.CHARGING ||
      chargingState === DEVICE_CONSTANTS.CHARGING_STATE.CHARGED)
  );
}

export function is4GDevice(deviceModelId?: DeviceModelId) {
  return deviceModelId && deviceModelId >= 8;
}
